import { Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KycActionButtons } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	AcceptanceStatus,
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { useUpdateKycFileStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useUpdateKycFileStatus';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { SelectedTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types';
import { DecisionCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/DecisionCard';
import { RejectionConfirmation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/RejectionConfirmation';
import { getDecisionAudit } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/utils/getDecisionAudit';
import { CertificationReviewPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/CertificationReviewPanel';
import {
	hasCertificationPanel,
	isCertificationOutstanding,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/utils/certificationReview.utils';
import { useResendCertifierInvitation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useResendCertifierInvitation';
import { useRejectionState } from '@dealstack/frontend/shared/hooks/useRejectionState';

interface Props {
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	currentStep: Step;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	isReadOnly?: boolean;
	selectedTemplate?: SelectedTemplate;
}

export const ReviewKycFiles = ({
	currentInvestor,
	currentStep,
	selectedKycFile,
	isReadOnly = false,
	selectedTemplate,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);
	const { updateKycFileStatus, mutation } = useUpdateKycFileStatus(getKYCDataRequest);
	const resendMutation = useResendCertifierInvitation(getKYCDataRequest);

	const isFormStep = currentStep?.type === StepType.FORM;
	const isInheritedFile = selectedKycFile?.subjectAction === SubjectAction.NOT_YET_ACTED;
	const isLoading = mutation.isPending || getKYCDataRequest.isFetching;

	const {
		isRejecting,
		comment,
		commentError,
		handleCommentChange,
		handleConfirmReject,
		handleCancelReject,
		handleDecline,
		reset,
	} = useRejectionState({
		emptyCommentError: tMipManage('kyc.review.add_comment_error'),
		onConfirm: async (rejectionComment) => {
			if (!selectedKycFile) {
				return;
			}
			await updateKycFileStatus({
				kycId: selectedKycFile.id,
				body: { status: AcceptanceStatus.REREQUESTED, rejectionComment },
			});
		},
	});

	const audit = useMemo(() => {
		if (!selectedKycFile?.type || !selectedKycFile.id || !currentInvestor?.kycHistory) {
			return {};
		}
		const events = currentInvestor.kycHistory[selectedKycFile.type] ?? [];
		const scoped = events.filter((event) => event.kycFileId === selectedKycFile.id);
		return getDecisionAudit(scoped);
	}, [selectedKycFile?.type, selectedKycFile?.id, currentInvestor?.kycHistory]);

	if (isFormStep && (selectedTemplate || !selectedKycFile)) {
		return null;
	}

	if (!selectedKycFile) {
		return null;
	}

	const status = selectedKycFile.status ?? AcceptanceStatus.PENDING;
	const isApproved = status === AcceptanceStatus.APPROVED;
	const isRejected =
		status === AcceptanceStatus.REJECTED || status === AcceptanceStatus.REREQUESTED;

	const certificationPanel = hasCertificationPanel(selectedKycFile) ? (
		<CertificationReviewPanel
			kycFile={selectedKycFile}
			isReadOnly={isReadOnly}
			isResending={resendMutation.isPending}
			onResend={() => resendMutation.mutate(selectedKycFile.id)}
		/>
	) : null;

	if (isApproved || isRejected) {
		return (
			<Flex direction="column" gap="1rem">
				<DecisionCard
					status={status}
					auditedBy={audit.auditedBy}
					timestamp={audit.timestamp}
					rejectionComment={selectedKycFile.rejectionComment}
					onUpdateDecision={async () => {
						await updateKycFileStatus({
							kycId: selectedKycFile.id,
							body: { status: AcceptanceStatus.PENDING, rejectionComment: '' },
						});
						reset();
					}}
					isLoading={isLoading}
					disabled={isReadOnly || isInheritedFile}
				/>
				{certificationPanel}
			</Flex>
		);
	}

	const handleApprove = async () => {
		await updateKycFileStatus({
			kycId: selectedKycFile.id,
			body: { status: AcceptanceStatus.APPROVED, rejectionComment: '' },
		});
		reset();
	};

	return (
		<Flex direction="column" gap="1rem">
			<KycActionButtons
				currentInvestor={currentInvestor}
				currentStep={currentStep}
				selectedKycFile={selectedKycFile}
				loading={isLoading}
				isDisabled={isReadOnly || isInheritedFile}
				isApproveDisabled={isCertificationOutstanding(selectedKycFile)}
				onApprove={handleApprove}
				onDecline={handleDecline}
			/>
			{certificationPanel}
			{isInheritedFile && (
				<Flex align="center" gap="0.5rem" c="dark.2">
					<Icon variant="clock-time-watch" />
					<Text size="B6">{tMipManage('kyc.review.inherited_file_hint')}</Text>
				</Flex>
			)}
			{isRejecting && !isReadOnly && (
				<RejectionConfirmation
					comment={comment}
					commentError={commentError}
					isLoading={isLoading}
					disabled={isInheritedFile}
					onCommentChange={handleCommentChange}
					onCancel={handleCancelReject}
					onConfirm={handleConfirmReject}
				/>
			)}
		</Flex>
	);
};
